<template>
  <CustomModal
    :visible="modelValue"
    @update:visible="$emit('update:modelValue', $event)"
    :title="props.title || title"
    :subTitle="props.subTitle"
    width="30vw"
    :section-classes="{
      subTitle: 'secondary_color',
    }"
  >
    <div class="flex flex-col gap-4">
      <div id="emails" class="form_col" v-if="!isEditing">
        <span class="secondary_color text-[13px]">Emails</span>
        <a-select
          v-model:value="form.emails"
          mode="tags"
          placeholder="Enter email addresses"
          class="w-full"
          :show-arrow="false"
          :open="false"
          :tokenSeparators="[',', ' ', 'Enter']"
          autoFocus
          ref="emailInputRef"
          :class="{ 'error-select': errorMessage }"
          @change="handleEmailChange"
          @search="handleEmailSearch"
        ></a-select>

        <span v-if="errorMessage" class="error_message_color text-xs mt-1">{{
          errorMessage
        }}</span>
      </div>

      <div id="roles" class="form_col">
        <span class="secondary_color text-[13px]">Role</span>
        <a-select
          v-model:value="form.role_id"
          class="w-full"
          placeholder="Select Role"
        >
          <a-select-option
            v-for="role in roles"
            :key="role.id"
            :value="role.id"
          >
            {{ role.role_name }}
          </a-select-option>
        </a-select>
      </div>

      <div v-if="isAppStoreV2 && !isEditing" class="form_col">
        <span class="secondary_color text-[13px]">Apps </span>

        <div v-if="loadingApps" class="flex justify-center items-center py-8">
          <CustomLoader :size="24" />
        </div>

        <SimpleTable
          v-else-if="installedApps.length > 0"
          :headers="appHeaders"
          :data="installedApps"
          :row-selection="true"
          :selection="appSelection"
          identifier-key="id"
          :height="200"
          :disable-horizontal-scroll="true"
          prefix-border-radius="6px"
          @update:selection="handleAppSelection"
        />

        <EmptyScreen
          v-else
          source="invite_apps_empty"
          text="No apps installed"
          padding="py-8"
        />
      </div>
    </div>

    <template #footer>
      <CustomAlerts
        v-if="
          billingVersion === billingVersions.V1 &&
          billingStatus === billingVersions.V3 &&
          additionalSeatsNeeded > 0 &&
          userSeatEnabled &&
          billingStatus === billingStatues.PAID &&
          !isEditing
        "
        source="user_modal"
        type="warning"
      >
        <div
          id="additional_seats_warning"
          class="flex flex-row items-center gap-2"
        >
          <span class="text-sm font-normal leading-[1.2]">
            No seats remaining. Additional {{ additionalSeatsNeeded }} seat{{
              additionalSeatsNeeded > 1 ? "s" : ""
            }}
            will be automatically added to your subscription and charged
          </span>

          <a-button
            @click="handleOk"
            class="primary_medium"
            :disabled="isDisabled"
          >
            Invite
          </a-button>
        </div>
      </CustomAlerts>

      <!--      <CustomAlerts-->
      <!--        v-else-if="-->
      <!--          billingVersion === billingVersions.V1 &&-->
      <!--          !hasPaidPlan &&-->
      <!--          userSeatEnabled-->
      <!--        "-->
      <!--        source="user_modal"-->
      <!--        type="warning"-->
      <!--      >-->
      <!--        <div id="setup_billing" class="flex flex-row items-center gap-2">-->
      <!--          <span class="text-sm font-normal leading-[1.2]">-->
      <!--            Please setup your billing to add more team members-->
      <!--          </span>-->

      <!--          <a-button @click="moveToBilling(false)" class="primary_medium">-->
      <!--            Setup Billing-->
      <!--          </a-button>-->
      <!--        </div>-->
      <!--      </CustomAlerts>-->

      <a-button
        v-else
        class="primary_medium"
        :disabled="isDisabled"
        @click="handleOk"
      >
        {{ isEditing ? "Update" : "Invite" }}
      </a-button>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import { isAppStoreV2 as isV2 } from "@/src/utils/appStoreUtils.js";
import { message } from "ant-design-vue";
import { moveToBilling } from "@/src/utils/utils.js";
import {
  billingStatues,
  billingVersions,
} from "@/src/constants/billingConstants.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import DashboardService from "@/src/api/DashboardService.js";
import AuthPermissionService from "@/src/api/AuthPermissionService.js";
import { isABACEnabled } from "@/src/utils/dynamicFormUtils.js";
import AppStoreService from "@/src/api/AppStoreService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomAlerts from "@/src/components/CustomAlerts.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import BillingService from "@/src/api/BillingService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";

const props = defineProps({
  roles: { type: Array, required: true },
  users: { type: Object, required: false },
  title: { type: String, required: false },
  subTitle: { type: String, required: false },
  userId: { type: Number, required: false },
  userCount: { type: Number, required: false },
  isEditing: { type: Boolean, required: true },
  modelValue: { type: Boolean, required: true },
  initialRoleId: { type: Number, required: false },
});

const emit = defineEmits(["update:modelValue"]);
const emailInputRef = ref(null);
const errorMessage = ref("");
const form = ref({
  emails: [],
  role_id: null,
});

const installedApps = ref([]);
const loadingApps = ref(false);
const selectedAppIds = ref([]);

const createEmptySelection = () => ({
  select_all: false,
  selected_ids: [],
  unselected_ids: [],
});

const appSelection = ref(createEmptySelection());

const appHeaders = ref([
  {
    label: "Apps",
    key: "title",
    prefix: "app_logo_url",
    width: 12,
  },
]);

const title = computed(() => (props.isEditing ? "Edit User" : "Invite Users"));

const isAppStoreV2 = computed(() => isV2(store));

const handleEmailChange = () => {
  errorMessage.value = "";
};

const handleEmailSearch = () => {
  errorMessage.value = "";
};

const resetAppSelection = () => {
  selectedAppIds.value = [];
  appSelection.value = createEmptySelection();
};

const handleAppSelection = (selection) => {
  console.log("[UserModal] handleAppSelection called with:", selection);
  appSelection.value = selection;
  selectedAppIds.value = selection.selected_ids || [];
  console.log("[UserModal] selectedAppIds updated to:", selectedAppIds.value);
};

const fetchInstalledApps = async () => {
  try {
    loadingApps.value = true;
    const response = await AppStoreService.getInstalledApps();
    if (response?.success && response?.installed_apps) {
      installedApps.value = response?.installed_apps?.map((item) => ({
        id: item?.app?.id,
        title: item?.app?.title,
        app_logo_url: item?.app?.app_logo_url,
        short_description: item?.app?.short_description,
        app_code: item?.app?.app_code,
      }));
    }
  } catch (error) {
    console.error("Error fetching installed apps:", error);
  } finally {
    loadingApps.value = false;
  }
};

const humanSeats = computed(() => store.getters["billing/humanSeats"] || 0);

const billingStatus = computed(() => store.getters["billing/status"]);
const userSeatEnabled = computed(
  () => store.getters["billing/userSeatEnabled"]
);
const hasPaidPlan = computed(
  () => store.getters["billing/hasPaidPlan"] || false
);
const billingVersion = computed(
  () => store.getters["billing/billingVersion"] || billingVersions.V0
);

// Calculate additional seats needed for paid billing
const additionalSeatsNeeded = computed(() => {
  if (
    !form.value.emails.length ||
    billingStatus.value !== billingStatues.PAID
  ) {
    return 0;
  }
  const remainingSeats = Math.max(0, humanSeats.value - props.userCount);
  const seatsNeeded = form.value.emails.length - remainingSeats;
  return Math.max(0, seatsNeeded);
});

const isDisabled = computed(() => {
  if (billingVersion.value === billingVersions.V0) {
    return !props.isEditing && !form.value.emails.length;
  } else if (billingVersion.value === billingVersions.V1) {
    // For paid billing, allow invitations even if additional seats are needed
    if (billingStatus.value === billingStatues.PAID) {
      return !props.isEditing && !form.value.emails.length;
    }
    // For non-paid billing, keep original logic
    return (
      !props.isEditing &&
      !form.value.emails.length &&
      humanSeats.value > props.userCount
    );
  } else if (billingVersion.value === billingVersions.V3) {
    return !props.isEditing && !form.value.emails.length;
  } else if (billingVersion.value === billingVersions.V4) {
    return !props.isEditing && !form.value.emails.length;
  }
  return true;
});

const handleOk = async () => {
  try {
    if (!form.value.role_id) {
      message.error("Please select a role");
      return;
    }

    if (props.isEditing) {
      await updateUser();
    } else {
      const existingEmails = form.value.emails.filter((email) =>
        props.users.some(
          (user) => user.email.toLowerCase() === email.toLowerCase()
        )
      );

      if (existingEmails.length > 0) {
        errorMessage.value = `Email${existingEmails.length > 1 ? "s" : ""} already in use: ${existingEmails.join(", ")}`;
        return;
      }

      await createUser();
    }
  } catch (error) {
    console.error("An error occurred while handling ok: ", error);
  }
};

const createUser = async () => {
  try {
    if (!form.value.emails.length) {
      message.error("Please enter email addresses");
      return;
    }

    try {
      const emailPromises = form.value.emails.map((email) =>
        DashboardService.checkEmail(email)
      );

      const emailResults = await Promise.all(emailPromises);
      const hasErrors = emailResults.some((result) => result?.error);

      if (hasErrors) {
        errorMessage.value = emailResults
          .filter((result) => result?.error)
          .map((result) => result.error)
          .join(", ");
        return;
      }

      let isPaymentDue = await checkPaymentDue();
      if (isPaymentDue === true && additionalSeatsNeeded.value > 0) {
        console.error(
          "Your payment is due. Please complete payment to invite users."
        );
        message.error(
          "Your payment is due. Please complete payment to invite users."
        );
        return;
      }

      const invitePayload = {
        emails: form.value.emails,
      };
      
      if (isABACEnabled()) {
        invitePayload.role_uuid = form.value.role_id;
      } else {
        invitePayload.role_id = form.value.role_id;
      }
      
      if (isAppStoreV2.value && selectedAppIds?.value.length > 0) {
        invitePayload.app_ids = selectedAppIds.value;
      }

      const response = await DashboardService.sendTeamInvites(invitePayload);

      if (response?.success) {
        message.success("Invitations sent successfully");
        emit("update:modelValue", false);
        // Refresh users in store for ABAC conditional permissions
        await store.dispatch("users/fetchUsers");
        eventBus.emit("user-altered");
      } else if (response?.payment_failed === true) {
        console.error("Payment failed. Unable to invite users");
        message.error("Payment failed. Unable to invite users");
      } else {
        message.error(response?.message || "Failed to send invitations");
      }
    } catch (error) {
      message.error("An error occurred while sending the invitations");
    }
  } catch (error) {
    console.error("An error occurred while creating user: ", error);
  }
};

const checkPaymentDue = async () => {
  try {
    const response = await BillingService.checkPaymentDue();
    return response?.success && response?.payment_failed === true;
  } catch (error) {
    console.error("Error fetching payment due status:", error);
  }
};

const updateUser = async () => {
  try {
    if (isABACEnabled()) {
      await AuthPermissionService.updateRoleMembershipByUser(props.userId, {
        role_id: form.value.role_id,
      });

      message.success("User role updated successfully");
    } else {
      const response = await DashboardService.updateUserRole(props.userId, {
        role_id: form.value.role_id,
      });

      if (response.success) {
        message.success("User role updated successfully");
      } else {
        message.error("Failed to update user role");
      }
    }

    await Promise.all([
      store.dispatch("users/fetchUsers"),
      store.dispatch("users/fetchUsersWithDeleted"),
    ]);
    eventBus.emit("user-altered");
    emit("update:modelValue", false);
  } catch (error) {
    console.error("Error updating user role:", error);
    message.error("An error occurred while updating user role");
  }
};

watch(
  () => props.modelValue,
  async (newValue) => {
    if (newValue) {
      if (props.isEditing && isABACEnabled() && props.userId) {
        try {
          const membershipResponse =
            await AuthPermissionService.getUserRoleMembership(props.userId);
          if (membershipResponse?.data?.attributes?.role_id) {
            form.value.role_id = membershipResponse.data.attributes.role_id;
          } else {
            form.value.role_id = props.initialRoleId;
          }
        } catch (error) {
          console.error("Error fetching ABAC role for user:", error);
          form.value.role_id = props.initialRoleId;
        }
      } else {
        form.value.role_id = props.isEditing ? props.initialRoleId : null;
      }

      form.value.emails = [];
      resetAppSelection();
      errorMessage.value = "";

      if (!props.isEditing) {
        await fetchInstalledApps();
      }
    } else {
      installedApps.value = [];
      resetAppSelection();
      loadingApps.value = false;
    }
  },
  { immediate: true }
);
</script>

<style scoped>
:deep(.error-select .ant-select-selector) {
  border: 1px solid var(--error-color) !important;
}
</style>
